import React, { useState, useMemo } from "react";
import {
  Box,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import {
  selectUserFullName,
  selectUserRoleId,
} from "../../../store/selectors/authSelectors";
import { logoutAction } from "../../../store/actions/authActions";
import { ROUTES } from "../../../constants";
import { UserRole } from "../../../constants";
import {
  AppLogoIcon,
  DashboardIcon,
  PatientsIcon,
  LogOutIcon,
  PersonIcon,
  AppointmentSuccessIcon,
  AddPersonIcon,
  SettingsIcon,
  AuditsIcon,
} from "../../Icons";
import {
  LogoContainer,
  NavList,
  PersonContainer,
  SidebarContainer,
} from "./Sydebar.styled";
import { colors } from "../../../theme/colors";
import IconContainer from "../../Icons/IconContainer";
import { Modal } from "../../Modal";
import { Link } from "react-router-dom";

const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const userFullName = useSelector(selectUserFullName);
  const userRoleId = useSelector(selectUserRoleId);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const isManager = userRoleId === UserRole.Manager;

  const menuItems = useMemo(() => {
    const items: Array<{
      icon: React.FC<{ fill?: string }>;
      text: string;
      path: string;
    }> = [
      { icon: DashboardIcon, text: "Dashboard", path: ROUTES.DASHBOARD },
      {
        icon: AppointmentSuccessIcon,
        text: "Sessions",
        path: ROUTES.SESSIONS,
      },
      {
        icon: PatientsIcon,
        text: "Patients",
        path: ROUTES.PATIENTS,
      },
    ];

    if (isManager) {
      items.push(
        {
          icon: AddPersonIcon,
          text: "User Management",
          path: ROUTES.USER_MANAGEMENT,
        },
        {
          icon: SettingsIcon,
          text: "Manage Organization",
          path: ROUTES.MANAGE_ORGANIZATION,
        },
        { icon: AuditsIcon, text: "Audit Logs", path: ROUTES.AUDIT_LOGS },
      );
    }

    return items;
  }, [isManager]);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await dispatch(logoutAction()).unwrap();
      navigate(ROUTES.LOGIN);
    } catch (error) {
      // Even if logout fails, redirect to login
      navigate(ROUTES.LOGIN);
    } finally {
      setShowLogoutModal(false);
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const handleProfileClick = () => {
    navigate(ROUTES.PROFILE);
  };

  return (
    <SidebarContainer>
      <LogoContainer>
        <Link to="/">
          <AppLogoIcon height="40px" width="188px" />
        </Link>
      </LogoContainer>
      <PersonContainer onClick={handleProfileClick}>
        <IconContainer>
          <PersonIcon />
        </IconContainer>
        <Box>
          <Typography variant="body1" fontWeight={600}>
            {userFullName || "User"}
          </Typography>
        </Box>
      </PersonContainer>

      <Divider sx={{ margin: "32px 0" }} />

      <NavList>
        {menuItems.map((item, index) => (
          <ListItemButton
            key={index}
            onClick={() => handleNavigation(item.path)}
            selected={location.pathname === item.path}
            sx={{
              "&.Mui-selected": {
                backgroundColor: "transparent",
                "& span": {
                  color: `${colors.text.action}`,
                },
                "&:hover": {
                  backgroundColor: "transparent",
                },
              },
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <ListItemIcon>
              <item.icon fill={colors.primary.main} />
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        ))}
      </NavList>

      <Box sx={{ marginTop: "auto" }}>
        <ListItemButton onClick={handleLogoutClick}>
          <ListItemIcon sx={{ background: colors.background.red }}>
            <LogOutIcon />
          </ListItemIcon>
          <ListItemText primary={t("logoutConfirmation.confirm")} />
        </ListItemButton>
      </Box>

      <Modal
        open={showLogoutModal}
        onClose={handleLogoutCancel}
        title={t("logoutConfirmation.title")}
        primaryButtonText={t("logoutConfirmation.confirm")}
        secondaryButtonText={t("logoutConfirmation.cancel")}
        onPrimaryClick={handleLogoutConfirm}
        onSecondaryClick={handleLogoutCancel}
      >
        <div />
      </Modal>
    </SidebarContainer>
  );
};

export default Sidebar;
